import type { Me } from '@cliqcrm/contracts';
import { podeNaSessao } from '@/shared/sessao/contexto';
import type { SecaoDaAdministracao } from '../config/secoes-da-administracao';
import { AREAS, GESTAO, type Area } from '../config/areas';

/**
 * O submenu de Configurações aparece?
 *
 * Existe como função nomeada por causa do defeito que ela conserta: a condição
 * usava `recolhido` (a PREFERÊNCIA de quem recolheu o trilho) no lugar de
 * `mostrandoRecolhido` (o trilho estar estreito AGORA, que é falso enquanto se
 * espia). As duas são booleanos parecidos com nomes parecidos, e o erro não
 * aparece em tela nenhuma até alguém recolher o menu e entrar em Configurações.
 */
export function mostraSubmenuDeConfiguracoes(area: string, mostrandoRecolhido: boolean): boolean {
  return area === 'configuracoes' && !mostrandoRecolhido;
}

/**
 * A área de automações abre de verdade para esta pessoa (#897)?
 *
 * Precisa das duas coisas: a organização com a funcionalidade `automacoes`
 * ligada e a permissão de ler automações (ADR-0021, #732). Sem uma delas, o
 * item continua com o "Em breve" e a lista de espera.
 */
export function automacoesLiberadas(
  me: Pick<Me, 'permissoes' | 'flags'> | null | undefined,
): boolean {
  return me?.flags['automacoes'] === true && podeNaSessao(me, 'automacao', 'ler');
}

/** As áreas da gestão de organizações: o item "Organizações" acende em todas. */
export const AREAS_DA_GESTAO: ReadonlySet<string> = new Set([
  'organizacoes',
  'organizacao',
  'nova-organizacao',
  'planos',
]);

/** O nome que a barra móvel mostra para a área aberta. */
export function nomeDaArea(area: string): string {
  // O item do menu é "Comercial", mas a tela aberta é o Funil.
  if (area === 'funil') return 'Funil';
  const item = [...AREAS, ...GESTAO].find((a) => a.chave === area);
  if (item) return item.nome;
  if (area === 'feedbacks') return 'Feedback';
  if (area === 'etapas-do-funil') return 'Funil · Configurar etapas';
  if (AREAS_DA_GESTAO.has(area)) return 'Organizações';
  return area === 'cliente' ? 'Cliente' : 'Início';
}

/** As seções de Configurações que a pessoa vê, com o nome de cada uma. */
type Secoes = readonly { chave: SecaoDaAdministracao; nome: string }[];

/**
 * O título da barra do celular. Em Configurações ela diz também QUAL seção —
 * "Configurações · Pessoas". Sem isso ela dizia "Configurações" nas oito, e no
 * celular, onde o submenu vive dentro da gaveta, era a única pista de onde a
 * pessoa caiu.
 */
export function tituloDaBarra(
  area: Area,
  secaoDaAdmin: SecaoDaAdministracao,
  secoes: Secoes,
): string {
  return area === 'configuracoes'
    ? `Configurações · ${secoes.find((s) => s.chave === secaoDaAdmin)?.nome ?? ''}`
    : nomeDaArea(area);
}

/** O nome da tela que vai junto com o feedback (#561). */
export function nomeDaTelaParaFeedback(
  area: Area,
  secaoDaAdmin: SecaoDaAdministracao,
  secoes: Secoes,
): string {
  return area === 'configuracoes'
    ? `Configurações › ${secoes.find((s) => s.chave === secaoDaAdmin)?.nome ?? secaoDaAdmin}`
    : nomeDaArea(area);
}
